import { useEffect, useRef, useState } from "react";
import type { CheckinVideo } from "@/types";
import { CHECKIN_PHASE_ICONS, checkinClock, formatBytes, phaseLabel } from "@/lib/walkaround";
import { signedCheckinUrl } from "@/lib/checkinMedia";
import AiFaultReport from "./AiFaultReport";

interface CheckinPlayerProps {
  checkin: CheckinVideo;
  onClose: () => void;
}

export default function CheckinPlayer({ checkin, onClose }: CheckinPlayerProps) {
  const frames = checkin.frames ?? [];
  const hasVideo = !!checkin.video_path;
  const videoRef = useRef<HTMLVideoElement>(null);

  const [signedUrl, setSignedUrl] = useState("");
  const [loadingUrl, setLoadingUrl] = useState(hasVideo);
  const [urlError, setUrlError] = useState("");
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  // Mint a fresh signed URL for the stored clip.
  useEffect(() => {
    let active = true;
    if (!checkin.video_path) {
      setLoadingUrl(false);
      return undefined;
    }
    setLoadingUrl(true);
    signedCheckinUrl(checkin.video_path)
      .then((url) => {
        if (active) {
          setSignedUrl(url);
          setUrlError("");
        }
      })
      .catch((err) => {
        if (active) setUrlError(err instanceof Error ? err.message : "This clip couldn't be loaded.");
      })
      .finally(() => {
        if (active) setLoadingUrl(false);
      });
    return () => {
      active = false;
    };
  }, [checkin.video_path]);

  // Legacy stills slideshow (only used when there's no stored video).
  useEffect(() => {
    if (hasVideo || !playing || frames.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev >= frames.length - 1 ? 0 : prev + 1));
    }, 1600);
    return () => window.clearInterval(timer);
  }, [hasVideo, playing, frames.length]);

  const current = frames[index] ?? frames[0];
  const duration = checkin.duration_seconds ?? 0;

  const seekTo = (at: number) => {
    const el = videoRef.current;
    if (el && hasVideo) {
      try {
        el.currentTime = at;
        el.play().catch(() => undefined);
      } catch {
        // ignore seek issues
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex flex-col bg-background-50/98 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-3 border-b border-background-200/70 px-4 py-3.5 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
            <i className={`${CHECKIN_PHASE_ICONS[checkin.phase] ?? "ri-video-line"} text-lg`}></i>
          </span>
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-semibold text-foreground-950">
              {phaseLabel(checkin.phase)} walkaround
            </p>
            <p className="flex flex-wrap items-center gap-x-2 truncate text-xs text-foreground-600">
              <span>{checkin.recorder_name || "Gari host"}</span>
              <span className="text-foreground-500">·</span>
              <span>{checkinClock(duration)}</span>
              {hasVideo && checkin.video_size ? (
                <>
                  <span className="text-foreground-500">·</span>
                  <span>{formatBytes(checkin.video_size)}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-2 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/50"
        >
          <i className="ri-close-line"></i> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">
        <div className="mx-auto w-full max-w-3xl">
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-background-200/70 bg-background-200/50">
            {hasVideo && signedUrl ? (
              <video
                ref={videoRef}
                src={signedUrl}
                controls
                autoPlay
                playsInline
                className="absolute inset-0 h-full w-full bg-black object-contain"
              />
            ) : hasVideo && loadingUrl ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-foreground-600">
                <i className="ri-loader-4-line animate-spin text-2xl"></i>
                <p className="text-sm">Securing the clip…</p>
              </div>
            ) : hasVideo && urlError ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center">
                <i className="ri-error-warning-line text-2xl text-secondary-400"></i>
                <p className="text-sm text-foreground-600">{urlError}</p>
              </div>
            ) : current ? (
              <>
                <img
                  src={current.url}
                  alt={current.label}
                  title={`Check-in — ${current.label}`}
                  className={`absolute inset-0 h-full w-full object-cover object-top ${playing ? "animate-kenburns" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setPlaying((prev) => !prev)}
                  className="absolute bottom-3 right-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 text-lg text-foreground-900 backdrop-blur transition-colors hover:bg-background-50"
                  aria-label={playing ? "Pause" : "Play"}
                  title={playing ? "Pause" : "Play"}
                >
                  <i className={playing ? "ri-pause-fill" : "ri-play-fill"}></i>
                </button>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-foreground-500">
                <i className="ri-video-off-line text-3xl"></i>
              </div>
            )}

            <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/85 px-3 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur">
                <i className="ri-play-circle-line text-primary-500"></i> {hasVideo ? "Recorded clip" : "Still preview"}
              </span>
            </div>
          </div>

          {frames.length > 0 && (
            <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
              {frames.map((frame, frameIndex) => (
                <button
                  key={`${frame.label}-${frameIndex}`}
                  type="button"
                  onClick={() => (hasVideo ? seekTo(frame.at) : setIndex(frameIndex))}
                  className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${
                    !hasVideo && frameIndex === index ? "border-primary-500" : "border-background-200/70 opacity-70 hover:opacity-100"
                  }`}
                  title={`${frame.label} · ${checkinClock(frame.at)}`}
                >
                  <img src={frame.url} alt={frame.label} title={frame.label} className="h-full w-full object-cover object-top" />
                </button>
              ))}
            </div>
          )}

          {checkin.note && (
            <div className="mt-4 rounded-xl border border-background-200/70 bg-background-100 p-4">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-foreground-500">
                <i className="ri-sticky-note-line text-primary-500"></i> Condition notes
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-900">{checkin.note}</p>
            </div>
          )}

          {checkin.ai_report && (
            <div className="mt-4">
              <AiFaultReport report={checkin.ai_report} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}